/* eslint-disable indent */
// EXTERNAL DEPENDENCIES
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router'

// API
import { useProfileDetails } from '@/api/profile'

// REDUX
import {
  updateEditedProfileDataValues,
  updateEditPageErrorMessage,
  updateIsPreviewing,
  updateUneditedFormattedData,
} from '@/redux/features'

// COMPONENTS
import { BodyText, CaptionText, Card, Link, Row } from '@paypalcorp/pp-react'
import ProfilePhotosSection from './components/profile-photos-section'
import BusinessInfoSection from './components/business-info-section'
import {
  EditBizInfoPage,
  EditDescriptionPage,
  PreviewProfilePage,
  SaveEditsButton,
} from '..'

// HELPERS
import {
  useBusinessProfilePageContent,
  useBusinessProfileReduxState,
  useModularSellerViewPageContent,
} from '@/screens/business-profile-page/hooks'
import { scrollToTop, stringifyFptiProps, trackClick } from '@/utils'
import { formatRawProfileDetails } from '../../utils'

// HOOKS
import { useImpressionTracking } from '@/hooks'

// TEST IDS
import { OWNER_PROFILE_SCREEN_INDEX_CONTAINER_TEST_ID } from '@/screens/business-profile-page/constants/test-ids'

// FPTI
import {
  BP_EDIT_PREVIEW_BACK_CLICK_FPTI,
  BP_EDIT_PREVIEW_SAVE_CLICK_FPTI,
  BP_PREVIEW_BACK_CLICK_FPTI,
  BUSINESS_PROFILE_PAGE_SHOWN_TO_SELLER,
  EDIT_BIZ_INFO_SUCCESSFUL_SAVE_FPTI,
  EDIT_BP_PREVIEW_SHOWN_FPTI,
  EDIT_PROFILE_DESCRIPTION_SUCCESSFUL_SAVE_FPTI,
  PREVIEW_CHANGES_CONT_EDITING_CLICK_FPTI,
} from './constants/fpti'
import { PREVIEW_PROFILE_SCREEN_SHOWN_FPTI } from '../preview-profile-page/fpti'

// CONSTANTS
import { routes } from '@/api/constants'
import { commonModularProfileFptiProperties } from '@/constants'

// STYLES
import styles from './styles.module.less'
import { lightGrayColor } from '@/styles/colors'

/**
 * The OwnerProfilePage is the seller view of their own
 * profile with ability to edit fields
 */
export function OwnerProfilePage() {
  useImpressionTracking(BUSINESS_PROFILE_PAGE_SHOWN_TO_SELLER)

  // DECLARATIONS
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { handle } = useParams()
  const { pathname } = useLocation()

  // STATE
  const [currentPage, setCurrentPage] = useState<
    'biz_info_page' | 'biz_description_page'
  >(null)

  // CONTENT
  const getContent = useBusinessProfilePageContent()
  const getModularSellerViewPageContent = useModularSellerViewPageContent()
  const previewProfileSubheaderText = getContent('previewProfileSubheader')
  const previewChangesSubheaderText = getContent('previewChangesSubheader')
  const continueEditingText = getContent('continueEditing')
  const backToEditingText = getContent('backToEditing')
  const bizInfoUpdatedToastMessage = getModularSellerViewPageContent(
    'bizInfoUpdatedToast',
  )
  const profileDescriptionUpdatedToastMessage = getModularSellerViewPageContent(
    'profileDescriptionUpdatedToast',
  )

  const [currentSuccessToastMessage, { saveSuccessFpti }] = {
    biz_info_page: [
      bizInfoUpdatedToastMessage,
      {
        saveSuccessFpti: EDIT_BIZ_INFO_SUCCESSFUL_SAVE_FPTI,
      },
    ],
    biz_description_page: [
      profileDescriptionUpdatedToastMessage,
      {
        saveSuccessFpti: EDIT_PROFILE_DESCRIPTION_SUCCESSFUL_SAVE_FPTI,
      },
    ],
  }[currentPage] || ['', {}]

  // NETWORK REQUESTS
  const { data: profileDetails, isFetching } = useProfileDetails()
  const {
    isPreviewing,
    profileData,
    uneditedFormattedData,
    editedProfileData: { values },
  } = useBusinessProfileReduxState()

  const { businessName, businessHandle } = uneditedFormattedData
  const isLoading = isFetching && !businessName

  // EFFECTS START
  useEffect(() => {
    if (profileDetails && profileData) {
      const formattedProfileDetails = formatRawProfileDetails(
        profileDetails,
        profileData,
      )

      dispatch(updateUneditedFormattedData(formattedProfileDetails))

      dispatch(
        updateEditedProfileDataValues({
          ...formattedProfileDetails,
        }),
      )
    }
  }, [dispatch, profileDetails, profileData])

  useEffect(() => {
    scrollToTop()
  }, [isPreviewing])

  useEffect(() => {
    if (new RegExp(/\/edit\/bizinfo/i).test(pathname)) {
      setCurrentPage('biz_info_page')
    } else if (new RegExp(/\/edit\/description/i).test(pathname)) {
      setCurrentPage('biz_description_page')
    } else {
      setCurrentPage(null)
    }
  }, [pathname])
  // EFFECTS END

  /**
   * Come back to this page and reset any edited values with original data
   */
  const goBackToMainPage = () => {
    scrollToTop()
    dispatch(updateEditPageErrorMessage(''))
    dispatch(updateIsPreviewing(false))
    dispatch(updateEditedProfileDataValues(uneditedFormattedData))
    navigate(`${routes.BUSINESS_PROFILE_ROUTE}/${handle}`)
  }

  const stateToPreview = {
    ...uneditedFormattedData,
    businessDescription:
      values?.businessDescription === null ||
      uneditedFormattedData?.businessDescription === null
        ? ''
        : values?.businessDescription ||
          uneditedFormattedData?.businessDescription,
    contactInfo: {
      businessEmail:
        values.contactInfo.businessEmail === null
          ? null
          : values.contactInfo.businessEmail ||
            uneditedFormattedData?.contactInfo.businessEmail,
      businessPhoneNumber:
        values.contactInfo.businessPhoneNumber === null
          ? null
          : values.contactInfo.businessPhoneNumber ||
            uneditedFormattedData?.contactInfo.businessPhoneNumber,
      businessWebsiteUrl:
        values.contactInfo.businessWebsiteUrl === null
          ? null
          : values.contactInfo.businessWebsiteUrl ||
            uneditedFormattedData?.contactInfo.businessWebsiteUrl,
    },
    selectedLocationDisplayPreference:
      values?.profilePreferences?.locationDisplayPreference ||
      uneditedFormattedData?.profilePreferences?.locationDisplayPreference,
  }

  return (
    <>
      {isPreviewing ? (
        <PreviewProfilePage
          impressionToTrack={{
            ...(currentPage
              ? EDIT_BP_PREVIEW_SHOWN_FPTI
              : PREVIEW_PROFILE_SCREEN_SHOWN_FPTI),
            source: currentPage,
          }}
          customBackLinkText={currentPage ? backToEditingText : null}
          backLinkFpti={stringifyFptiProps(
            currentPage
              ? {
                  ...BP_EDIT_PREVIEW_BACK_CLICK_FPTI,
                  source: currentPage,
                }
              : BP_PREVIEW_BACK_CLICK_FPTI,
          )}
          subHeaderText={
            currentPage
              ? previewChangesSubheaderText
              : previewProfileSubheaderText
          }
          userCanCreateProfile={profileData?.userCanCreateProfile}
          stateToPreview={stateToPreview}
          CustomFooterButtons={
            currentPage
              ? () => (
                  <div className={styles.footerButtonsContainer}>
                    <SaveEditsButton
                      canSave={null}
                      successfulUpdateToastMessage={currentSuccessToastMessage}
                      saveButtonFpti={stringifyFptiProps({
                        ...BP_EDIT_PREVIEW_SAVE_CLICK_FPTI,
                        source: currentPage,
                      })}
                      successPublishFpti={saveSuccessFpti}
                      onSuccess={goBackToMainPage}
                    />
                    <Link
                      onClick={() => {
                        dispatch(updateIsPreviewing(false))
                        trackClick({
                          ...PREVIEW_CHANGES_CONT_EDITING_CLICK_FPTI,
                          source: currentPage,
                          ...commonModularProfileFptiProperties,
                        })
                      }}
                      tabIndex={0}
                    >
                      {continueEditingText}
                    </Link>
                  </div>
                )
              : null
          }
        />
      ) : (
        <>
          {currentPage === 'biz_info_page' && (
            <EditBizInfoPage goBackToMainPage={goBackToMainPage} />
          )}
          {currentPage === 'biz_description_page' && (
            <EditDescriptionPage goBackToMainPage={goBackToMainPage} />
          )}
          {!currentPage && (
            <div
              className={styles.profileContainer}
              data-testid={OWNER_PROFILE_SCREEN_INDEX_CONTAINER_TEST_ID}
            >
              <Card className={styles.profileCardContainer}>
                <ProfilePhotosSection isOwnerPhoto />
                <Card.Content className={styles.cardContentContainer}>
                  <Row noGutters className={styles.businessSectionContainer}>
                    <div>
                      <BodyText
                        strong
                        className={styles.businessSectionName}
                        isLoading={isLoading}
                      >
                        {businessName}
                      </BodyText>
                      <CaptionText
                        strong
                        className={styles.businessSectionHandle}
                        style={{ color: lightGrayColor }}
                        isLoading={isLoading}
                      >
                        {`@${businessHandle}`}
                      </CaptionText>
                    </div>
                  </Row>
                  <BusinessInfoSection isLoading={isLoading} />
                </Card.Content>
              </Card>
            </div>
          )}
        </>
      )}
    </>
  )
}
